import React from 'react';
import { useFormikContext } from 'formik';

import { useStyles } from '~/components/../assets/styles';
import useFetchClaimStageConfigurations from '~/components/ClaimStages/hooks/useFetchClaimStageConfigurations';
import { Text } from '~/components/core';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import useOrganization from '~/components/OrganizationContext';
import useFetchAutoCommRecipientOptions from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Communications/hooks/useFetchAutoCommRecipientOptions';
import { recipientBaseKey } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Communications/types';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { MultiSelectTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import COMMUNICATION_CHANNEL_DICT from '~/server_shared/generated-types/COMMUNICATION_CHANNEL_DICT';
import COMMUNICATION_DIRECTION from '~/server_shared/generated-types/COMMUNICATION_DIRECTION';
import { displayCommunicationCategories } from '~/Utils';

interface ConditionTypeOption {
  key: string;
  label: string;
}

interface CategoryOption {
  id: number;
  label: string;
  key: string;
  color: string;
}

interface TimeBasedCondition {
  criteria_key?: string | null;
  criteria_filters?: Record<string, unknown>;
  condition_schedule_value?: number | string;
}

interface FormValues {
  time_based_condition?: TimeBasedCondition | null;
  time_based_directions?: string[];
  time_based_channels?: string[];
  time_based_category_ids?: string[];
  time_based_stage_keys?: string[];
  time_based_recipient?: string[];
  [key: string]: unknown;
}

interface TimeBasedConditionFormikProps {
  disabled?: boolean;
}

// Time-based condition types
const TIME_BASED_CONDITION_TYPES: ConditionTypeOption[] = [
  { key: 'no_notes', label: 'No notes' },
  { key: 'no_communication', label: 'No communication' },
];

// String keys for direction options
const DIRECTION_OPTIONS: readonly string[] = COMMUNICATION_DIRECTION;

// String keys for channel options, with a lookup dict for labels
const CHANNEL_KEYS: string[] = Object.keys(COMMUNICATION_CHANNEL_DICT);

const ALL_KEY = 'all';

// The No-Communication condition runs claim-scoped, so the recipient filter is
// only meaningful for keys the BE can resolve to a contact at the claim level
// (`claim_recipient_contact_id_expr` in `server/notifications/recipient_resolution.py`).
// Allowing exposure-only keys (CLAIMANT, ATTORNEY) would let the user pick a
// filter the BE silently drops — and an all-exposure selection collapses the
// subquery to `false()`, matching every claim. Intersect with the claim subset.
const CLAIM_RESOLVABLE_RECIPIENT_KEYS: ReadonlySet<string> = new Set([
  'INSURED',
  'PREFERRED_CONTACT',
  'BROKER',
  'REPORTER',
]);

export const TimeBasedConditionFormik: React.FC<TimeBasedConditionFormikProps> = ({ disabled = false }) => {
  const { classes } = useStyles();
  const { values, setFieldValue } = useFormikContext<FormValues>();
  // useSysconfig returns an untyped object from legacy JS code
  const { organization } = useSysconfig() as { organization: { id: number } };
  // Get communication categories from OrganizationContext (already fetched at app level)
  const { communicationCategories = [] } = useOrganization() as { communicationCategories?: CategoryOption[] };

  // A saved rule can still filter on categories the organization has since disabled;
  // keep the field visible so the admin can see and clear that restriction.
  const hasSavedCategoryFilter = (values.time_based_category_ids || []).some((v) => v !== ALL_KEY);
  const showCategoryFilter = displayCommunicationCategories(communicationCategories) || hasSavedCategoryFilter;

  const { claimStageConfigurations } = useFetchClaimStageConfigurations({ organizationId: organization.id });
  const {
    recipientOptions,
    isLoading: isLoadingRecipientOptions,
    isError: isRecipientOptionsError,
  } = useFetchAutoCommRecipientOptions(organization.id);
  // On catalog fetch failure fall back to the full claim-resolvable allowlist
  // so the picker stays usable. Also union in any currently-selected recipients
  // so chips don't silently vanish if the BE drops a key (e.g. broker
  // `ContactRole` disabled after the rule was saved).
  const savedRecipients = (values.time_based_recipient || []).filter((v) => v !== ALL_KEY);
  const claimResolvableRecipients = isRecipientOptionsError
    ? []
    : recipientOptions.filter((opt) => CLAIM_RESOLVABLE_RECIPIENT_KEYS.has(opt.key));
  const fallbackKeys = isRecipientOptionsError ? Array.from(CLAIM_RESOLVABLE_RECIPIENT_KEYS) : [];
  const recipientKeys: string[] = Array.from(
    new Set([...claimResolvableRecipients.map((opt) => opt.key), ...fallbackKeys, ...savedRecipients])
  );
  // Seed labels from the static dict so saved-only keys (preserved chips after
  // the catalog drops them) and the `isError` fallback still render a friendly
  // label instead of the raw `BROKER` / `INSURED` key. Overlay BE `desc`s last
  // so the catalog wins when both are present.
  const recipientLabelMap: Record<string, string> = { ...recipientBaseKey };
  for (const opt of claimResolvableRecipients) {
    recipientLabelMap[opt.key] = opt.desc;
  }
  const stageKeys: string[] = claimStageConfigurations.filter((s) => s.is_enabled).map((s) => s.key);
  const stageLabelMap: Record<string, string> = {};
  for (const stage of claimStageConfigurations) {
    if (stage.is_enabled) {
      stageLabelMap[stage.key] = stage.label;
    }
  }

  const condition: TimeBasedCondition = values.time_based_condition || {};
  const criteriaKey = condition.criteria_key;
  const showCommunicationFilters = criteriaKey === 'no_communication';

  const categoryLabelMap: Record<string, string> = {};
  const categoryKeys: string[] = [];
  for (const cat of communicationCategories) {
    const k = String(cat.id);
    categoryKeys.push(k);
    categoryLabelMap[k] = cat.label;
  }

  const handleConditionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value || null;
    setFieldValue('time_based_condition', {
      ...condition,
      criteria_key: key,
      criteria_filters: key === 'no_communication' ? condition.criteria_filters || {} : {},
    });
    if (key !== 'no_communication') {
      setFieldValue('time_based_directions', [ALL_KEY]);
      setFieldValue('time_based_channels', [ALL_KEY]);
      setFieldValue('time_based_category_ids', [ALL_KEY]);
      setFieldValue('time_based_recipient', [ALL_KEY]);
    }
  };

  return (
    <div className="mb-16">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mb-12">
        Time-Based Trigger
      </Text>

      {/* Condition Type Selection */}
      <Grid container spacing={2} className="mb-16">
        <Grid item xs={6}>
          <TextFieldFormik
            id="time_based_condition.criteria_key"
            label="Condition Type"
            select
            // @ts-expect-error TextFieldFormik types don't support onChange prop
            onChange={handleConditionTypeChange}
            disabled={disabled}
            className={classes.formTextField}
            fullWidth
          >
            {/* @ts-expect-error TextFieldFormik types don't support children when used as select */}
            {TIME_BASED_CONDITION_TYPES.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextFieldFormik>
        </Grid>
        <Grid item xs={3}>
          <TextFieldFormik
            id="time_based_condition.condition_schedule_value"
            label="Days"
            disabled={disabled || !criteriaKey}
            className={classes.formTextField}
            fullWidth
            InputProps={{ inputProps: { min: 1, type: 'number' } }}
          />
        </Grid>
      </Grid>

      {/* Communication Filters (only for no_communication) */}
      {showCommunicationFilters && (
        <>
          <Text
            variant={Text.VARIANTS.XS}
            weight={Text.WEIGHTS.REGULAR}
            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
            className="mb-8"
          >
            Leave empty for all (no filter)
          </Text>
          <Grid container spacing={2}>
            {/* Direction Filter */}
            <Grid item xs={4}>
              <MultiSelectTextFieldFormik
                id="time_based_directions"
                label="Direction"
                options={[ALL_KEY, ...DIRECTION_OPTIONS]}
                renderValue={() => ''}
                renderOption={(value: string) => (value === ALL_KEY ? 'All' : value)}
                disabled={disabled}
                className={classes.formTextFieldNoErrorSpacing}
                withOptionChips
                fullWidth
              />
            </Grid>

            {/* Category Filter - shown once the organization has an active communication category, or the rule already filters on one */}
            {showCategoryFilter && (
              <Grid item xs={4}>
                <MultiSelectTextFieldFormik
                  id="time_based_category_ids"
                  label="Category"
                  options={[ALL_KEY, ...categoryKeys]}
                  renderValue={() => ''}
                  renderOption={(value: string) => (value === ALL_KEY ? 'All' : categoryLabelMap[value] || value)}
                  disabled={disabled}
                  className={classes.formTextFieldNoErrorSpacing}
                  withOptionChips
                  fullWidth
                />
              </Grid>
            )}

            {/* Communication Type (Channel) Filter */}
            <Grid item xs={4}>
              <MultiSelectTextFieldFormik
                id="time_based_channels"
                label="Type"
                options={[ALL_KEY, ...CHANNEL_KEYS]}
                renderValue={() => ''}
                renderOption={(value: string) =>
                  value === ALL_KEY ? 'All' : (COMMUNICATION_CHANNEL_DICT as Record<string, string>)[value] || value
                }
                disabled={disabled}
                className={classes.formTextFieldNoErrorSpacing}
                withOptionChips
                fullWidth
              />
            </Grid>

            {/* Recipient Filter — options sourced from the BE recipient catalog. */}
            <Grid item xs={4}>
              <MultiSelectTextFieldFormik
                id="time_based_recipient"
                label="Recipient"
                options={[ALL_KEY, ...recipientKeys]}
                renderValue={() => ''}
                renderOption={(value: string) => (value === ALL_KEY ? 'All' : recipientLabelMap[value] || value)}
                disabled={disabled || isLoadingRecipientOptions}
                className={classes.formTextFieldNoErrorSpacing}
                withOptionChips
                fullWidth
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Claim Stage Filter (for both no_notes and no_communication) */}
      {criteriaKey && stageKeys.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MultiSelectTextFieldFormik
              id="time_based_stage_keys"
              label="Claim Stage"
              options={[ALL_KEY, ...stageKeys]}
              renderValue={() => ''}
              renderOption={(value: string) => (value === ALL_KEY ? 'All' : stageLabelMap[value] || value)}
              disabled={disabled}
              className={classes.formTextFieldNoErrorSpacing}
              withOptionChips
              fullWidth
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
};
